// Публичная страница поездки: /s/<id>.
//
// Это отдельный экран только на чтение, а не планировщик в режиме «нельзя
// трогать»: у читателя нет ни аккаунта, ни своего состояния, и любая кнопка
// редактирования тут была бы обманом.
//
// Для большинства читателей это первая встреча с сервисом: пришла ссылка от
// друга, чаще всего в мессенджере и чаще всего на телефон. Отсюда порядок
// блоков — сначала ответ («во сколько обойдётся дешёвый вариант»), потом
// сравнение, потом из чего цена сложилась. Список перелётов сверху был бы
// ответом на вопрос, которого читатель не задавал.
import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import type { Currency, Segment, VariantTotals } from '../types';
import {
  computeVariantTotals,
  formatDuration,
  formatMoney,
  segmentAccent,
  segmentSubtitle,
  segmentTitle,
} from '../core';
import { LanguagePicker, useI18n, useT, type Translate } from '../i18n';
import { PersonalizationPanel } from '../theme';
import { AppMenu, AppMenuBlock, AppMenuRow, Logo } from '../ui/shell';
import { KindIcon } from '../ui/library';
import { COMPARE_METRICS, computeBestSet, maxWarningLevel } from '../ui/compare';
import { useRoute } from '../router';
import { useDocumentMeta } from '../seo';
import { fetchSharedTrip, type SharedTrip } from './api';
import { cheapestIndex, tripPreview } from './preview';
import styles from './SharedTripPage.module.css';

function warningColorVar(level: 'error' | 'warn' | null): string {
  if (level === 'error') return 'var(--error)';
  if (level === 'warn') return 'var(--warn)';
  return 'var(--text-3)';
}

/**
 * Дата на языке страницы, без секунд: `toLocaleString()` брал язык браузера, и
 * на испанской странице стояла дата в русском формате — ровно то, от чего
 * ссылку и лечили языком автора.
 */
function formatStamp(ms: number, lang: string): string {
  try {
    return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(ms);
  } catch {
    return new Date(ms).toLocaleString();
  }
}

/** Меню страницы: тулбара планировщика тут нет, а вид читателю менять надо. */
function ShareMenu(): ReactElement {
  const t = useT();
  return (
    <AppMenu
      sections={[
        {
          id: 'view',
          title: t('menu.section.view'),
          children: (
            <>
              <AppMenuRow label={t('app.language')}>
                <LanguagePicker />
              </AppMenuRow>
              {/* Ссылка приходит в теме и размерах автора (см. share/api.ts).
                  Это его страница, поэтому его вид и побеждает — но читателю
                  нужен выход: покрутил ползунок, и дальше сайт помнит уже его
                  выбор, а не присланный. */}
              <AppMenuBlock>
                <PersonalizationPanel />
              </AppMenuBlock>
            </>
          ),
        },
      ]}
    />
  );
}

/** Одна строка «подпись сверху, значение снизу» — как в итогах планировщика. */
function Figure({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className={styles.figure}>
      <dt className={styles.figureLabel}>{label}</dt>
      <dd className={`${styles.figureValue} mono`}>{value}</dd>
    </div>
  );
}

/**
 * Ответ на вопрос, с которым пришли по ссылке: сколько стоит самый дешёвый
 * вариант и что за него получаешь. Вариант тот же, что в описании ссылки, —
 * правило выбора одно на двоих (preview.cheapestIndex).
 */
function Answer({
  trip,
  totals,
  name,
  color,
}: {
  trip: SharedTrip;
  totals: VariantTotals;
  name: string;
  color: string;
}): ReactElement {
  const t = useT();
  const priced = totals.totalWithBaggageMinor > 0;
  return (
    <section
      className={styles.answer}
      style={{ '--variant-color': color } as CSSProperties}
      aria-label={t('share.cheapest')}
    >
      <div>
        <p className={styles.answerLabel}>{t('share.cheapest')}</p>
        {/* Ноль — это не «бесплатно», а «цены ещё не проставлены»: показать
            «$0.00» крупным кеглем значит соврать в самом заметном месте. */}
        {priced ? (
          <p className={`${styles.answerPrice} mono`}>
            {formatMoney(totals.totalWithBaggageMinor, totals.currency, trip.state.currencies)}
          </p>
        ) : (
          <p className={styles.answerNoPrice}>{t('share.noPrice')}</p>
        )}
        <p className={styles.answerVariant}>
          <span className={styles.dot} style={{ background: color }} aria-hidden="true" />
          {name}
        </p>
      </div>

      <dl className={styles.figures}>
        {totals.flightTimeMin > 0 && (
          <Figure label={t('totals.flightTime')} value={formatDuration(totals.flightTimeMin, t)} />
        )}
        {totals.doorToDoorMin > 0 && (
          <Figure label={t('totals.doorToDoor')} value={formatDuration(totals.doorToDoorMin, t)} />
        )}
        {totals.nights > 0 && <Figure label={t('totals.nights')} value={String(totals.nights)} />}
      </dl>
    </section>
  );
}

/**
 * Та же таблица, что в планировщике, но собранная из присланного состояния, а
 * не из стора: у читателя стора нет вовсе. Общее здесь — описание строк
 * (COMPARE_METRICS) и правило подсветки лучшего значения; ради этого таблица и
 * не переписана заново.
 */
function SharedCompare({ trip, totals }: { trip: SharedTrip; totals: VariantTotals[] }): ReactElement {
  const t = useT();

  // Строку предупреждений показываем, только если есть о чём предупреждать:
  // «Предупреждений: 0» в каждой колонке — шум, а постороннему человеку ещё и
  // намёк, что с поездкой что-то не так.
  const metrics = useMemo(() => {
    const anyWarnings = totals.some((row) => row.warnings.length > 0);
    return COMPARE_METRICS.filter((metric) => metric.key !== 'warnings' || anyWarnings);
  }, [totals]);

  return (
    <section className={styles.compare}>
      <h2 className={styles.sectionTitle}>{t('compare.title')}</h2>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <caption className="visually-hidden">{t('compare.caption')}</caption>
          <colgroup>
            <col className={styles.metricCol} />
            {trip.state.variants.map((v) => (
              <col key={v.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">{t('compare.metric')}</th>
              {trip.state.variants.map((v) => (
                <th scope="col" key={v.id}>
                  <span className={styles.variantHeader}>
                    <span className={styles.dot} style={{ background: v.color }} aria-hidden="true" />
                    <span className={styles.variantName} title={v.name}>
                      {v.name}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => {
              const bestSet = computeBestSet(totals, metric);
              // Все значения равны — «лучшего» нет: ряд галочек в каждой
              // колонке ничего не сравнивает, а выглядит как разметка ошибки.
              const marks = bestSet.size === totals.length ? null : bestSet;
              return (
                <tr key={metric.key}>
                  <th scope="row">{t(metric.labelKey)}</th>
                  {totals.map((row) => {
                    const formatted = metric.format(
                      metric.getValue(row),
                      row,
                      trip.state.currencies,
                      t,
                    );
                    const isBest = marks?.has(row.variantId) ?? false;
                    const isWarningsRow = metric.key === 'warnings';
                    const level = isWarningsRow ? maxWarningLevel(row) : null;
                    return (
                      <td
                        key={row.variantId}
                        className={isBest ? `mono ${styles.best}` : 'mono'}
                        style={isWarningsRow ? { color: warningColorVar(level) } : undefined}
                      >
                        {isBest && (
                          <>
                            {/* Подпись для скринридера — текстом внутри ячейки:
                                aria-label на <td> либо не читается, либо
                                подменяет собой само значение. */}
                            <span className="visually-hidden">
                              {t('compare.best', { value: formatted })}
                            </span>
                            <span className={styles.checkmark} aria-hidden="true">
                              ✓
                            </span>
                          </>
                        )}
                        {formatted}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Строка сегмента: иконка типа, название с подписью и цена у правого края. */
function SegmentRow({
  segment,
  price,
  currency,
  trip,
  t,
}: {
  segment: Segment;
  price: number;
  /** Валюта итогов варианта: цены сегментов уже сконвертированы в неё. */
  currency: Currency;
  trip: SharedTrip;
  t: Translate;
}): ReactElement {
  const subtitle = segmentSubtitle(segment, trip.state.currencies, t);
  return (
    <li className={styles.segment} data-accent={segmentAccent(segment.kind)}>
      <span className={styles.segIcon}>
        <KindIcon kind={segment.kind} size={12} />
      </span>
      <span className={styles.segText}>
        <span className={styles.segTitle}>{segmentTitle(segment, t)}</span>
        {subtitle && <span className={styles.segSubtitle}>{subtitle}</span>}
      </span>
      {/* Ноль — это прочерк, а не пустота: пустая ячейка дырявит колонку цен,
          и карточка перестаёт читаться как столбик чисел. Ноль тут бывает
          законный — обратное плечо RT-пары посчитано в партнёре. */}
      <span className={`${styles.segPrice} mono`}>
        {price > 0 ? (
          formatMoney(price, currency, trip.state.currencies)
        ) : (
          <span className={styles.segPriceZero}>—</span>
        )}
      </span>
    </li>
  );
}

export function SharedTripPage(): ReactElement {
  const { lang, t, suggestLang } = useI18n();
  const { path } = useRoute();
  const id = path.slice('/s/'.length);

  const [trip, setTrip] = useState<SharedTrip | null>(null);
  const [state, setState] = useState<'busy' | 'ok' | 'fail'>('busy');

  useEffect(() => {
    let cancelled = false;
    void fetchSharedTrip(id).then((res) => {
      if (cancelled) return;
      if (res.ok && res.value?.state) {
        setTrip(res.value);
        setState('ok');
      } else {
        setState('fail');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Язык того, кто поделился: испанец делится поездкой со своими, и открывший
  // ссылку скорее прочтёт её по-испански, чем на языке, угаданном по браузеру.
  // Свой выбор человека это не трогает — suggestLang молчит, если он был.
  // Ссылки нового образца несут язык прямо в адресе (?lang=), это запасной
  // путь для разосланных раньше.
  useEffect(() => {
    if (trip?.lang) suggestLang(trip.lang);
  }, [trip?.lang, suggestLang]);

  const title = trip ? trip.name.trim() || t('trips.untitled') : '';
  useDocumentMeta({
    title: state === 'fail' ? t('share.notFound') : title,
    description: trip ? tripPreview(trip.state, t) : undefined,
  });

  const totals = useMemo(() => {
    if (!trip) return [];
    return trip.state.variants.map((variant) => ({
      variant,
      totals: computeVariantTotals(variant, trip.state, t),
    }));
  }, [trip, t]);

  const best = cheapestIndex(totals.map((row) => row.totals));
  const bestRow = best >= 0 ? totals[best] : null;
  const hasTrip = state === 'ok' && totals.length > 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Logo title={t('app.brand')} href="/" />
        <span className={styles.badge}>{t('share.readonly')}</span>
        <ShareMenu />
      </header>

      <main className={styles.main}>
        {state === 'busy' && (
          <div className={styles.skeleton} aria-busy="true">
            {/* Заглушка без мерцания: единственная зона движения в проекте —
                физика dnd (tokens.css), и пульсирующий блок на странице,
                которую пришли прочитать, спорил бы с этим правилом. */}
            <span className="visually-hidden">{t('auth.busy')}</span>
            <div className={`${styles.skelBlock} ${styles.skelHero}`} />
            <div className={`${styles.skelBlock} ${styles.skelTable}`} />
          </div>
        )}

        {state === 'fail' && (
          <div className={styles.stateWrap}>
            <div className={styles.stateCard} role="status">
              <p className={styles.stateTitle}>{t('share.notFound')}</p>
              <a className="btn btn-primary" href="/">
                {t('share.makeYours')}
              </a>
            </div>
          </div>
        )}

        {trip && (
          <>
            <section className={styles.hero}>
              {/* Название поездки — заголовок страницы: читатель пришёл по
                  ссылке на неё, а не на приложение. */}
              <h1 className={styles.tripTitle}>{title}</h1>
              <ul className={styles.meta}>
                <li>{t('share.updated', { date: formatStamp(trip.updatedAt, lang) })}</li>
                <li>{t('share.travelers', { count: trip.state.travelers })}</li>
              </ul>
            </section>

            {bestRow && (
              <Answer
                trip={trip}
                totals={bestRow.totals}
                name={bestRow.variant.name}
                color={bestRow.variant.color}
              />
            )}

            {/* Сравнение стоит выше самих вариантов: по ссылке приходят за
                ответом «какой дешевле и быстрее», а не за списком перелётов. */}
            {totals.length > 1 && (
              <SharedCompare trip={trip} totals={totals.map((row) => row.totals)} />
            )}

            {totals.length > 0 && (
              <section className={styles.variants}>
                <h2 className={styles.sectionTitle}>{t('share.segments')}</h2>
                {/* Карточки идут в авторском порядке, а не по цене: колонки
                    таблицы выше — в том же порядке, и пересортировка порвала
                    бы связь «колонка ↔ карточка». Кто дешевле, говорит
                    бейдж. */}
                <div className={styles.cards} data-count={Math.min(totals.length, 4)}>
                  {totals.map(({ variant, totals: sums }, index) => (
                    <section
                      key={variant.id}
                      className={styles.card}
                      style={{ '--variant-color': variant.color } as CSSProperties}
                    >
                      <div className={styles.cardHead}>
                        <h3 className={styles.cardName}>{variant.name}</h3>
                        {index === best && totals.length > 1 && (
                          <span className={styles.cardBadge}>{t('share.cheapest')}</span>
                        )}
                      </div>

                      <div>
                        <p className={`${styles.cardPrice} mono`}>
                          {formatMoney(
                            sums.totalWithBaggageMinor,
                            sums.currency,
                            trip.state.currencies,
                          )}
                        </p>
                        <p className={styles.cardPriceNote}>{t('compare.totalWithBaggage')}</p>
                      </div>

                      <p className={styles.cardFigures}>
                        {sums.flightTimeMin > 0 && (
                          <span>
                            {/* Переводчик обязателен: без него суффиксы часов и
                                минут остаются русскими посреди чужого языка. */}
                            {t('compare.flightTime')}: {formatDuration(sums.flightTimeMin, t)}
                          </span>
                        )}
                        {sums.nights > 0 && (
                          <span>{t('summary.nights', { count: sums.nights })}</span>
                        )}
                      </p>

                      <ol className={styles.segments} role="list">
                        {variant.segmentIds.map((segmentId) => {
                          const segment = trip.state.library[segmentId];
                          if (!segment) return null;
                          return (
                            <SegmentRow
                              key={segmentId}
                              segment={segment}
                              price={sums.perSegmentMinor[segmentId] ?? 0}
                              currency={sums.currency}
                              trip={trip}
                              t={t}
                            />
                          );
                        })}
                      </ol>
                    </section>
                  ))}
                </div>
              </section>
            )}

            {totals.length === 0 && (
              <div className={styles.stateWrap}>
                <div className={styles.stateCard} role="status">
                  <p className={styles.stateTitle}>{t('share.empty')}</p>
                  <a className="btn btn-primary" href="/">
                    {t('share.makeYours')}
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Незнакомый человек, только что прочитавший чужую поездку, — самый
          тёплый читатель, какой у сервиса бывает: он уже видел, что тот
          умеет. Текстовая ссылка в 12 пикселей была здесь не предложением, а
          отпиской.
          Кнопки нет, когда её уже показала карточка состояния: два одинаковых
          призыва на пустой странице — это не вдвое убедительнее. */}
      <footer className={styles.footer}>
        <p className={styles.footerNote}>{t('share.preview.tail')}</p>
        {hasTrip && (
          <a className={`btn btn-primary ${styles.footerCta}`} href="/">
            {t('share.makeYours')}
          </a>
        )}
      </footer>
    </div>
  );
}
